<template>
  <v-container class="order-confirmation">
    <!-- META-DATA -->
    <vue-headful
      :title="'Order Confirmation' + this.$store.state.app.meta.titleSuffix"
      :description="this.$store.state.app.meta.description" />

    <m-h-logo
      width="75"
      class="order-confirmation__print-logo" />
    <h2 class="order-confirmation__header">Order Complete</h2>
    <div
      v-if="invoice"
      class="order-confirmation__body">
      <p>Your order has been placed and will be shipping soon! Keep an eye out for a confirmation email at <b>{{ invoice.emailAddress }}</b>.</p>
    </div>
    <p
      v-if="invoice && isAuthenticated"
      class="order-confirmation__track-order-row">
      You can also keep tabs on your order through your
      <router-link :to="{ name: 'order-history' }">
        memerhats.com account
      </router-link>
    </p>
    <div
      v-if="invoice"
      class="invoice">
      <div class="invoice__header">
        <div class="invoice__order-number">
          <span class="invoice__subheader">order number:</span>
          <span>{{ invoice.orderNumber }}</span>
        </div>
        <div class="invoice__shipping-address">
          <span class="invoice__subheader">shipping address:</span>
          <div class="address">
            <span class="address__name">
              {{ invoice.shippingAddress.fullName }}
            </span>
            <span class="address__company">
              {{ invoice.shippingAddress.company }}
            </span>
            <span class="address__address1">
              {{ invoice.shippingAddress.line1 }}
            </span>
            <span class="address__address2">
              {{ invoice.shippingAddress.line2 }}
            </span>
            <div class="address__city-state-zip">
              <span class="address__city">
                {{ invoice.shippingAddress.city }},
              </span>
              <span class="address__state">
                {{ invoice.shippingAddress.state }}
              </span>
              <span class="address__zip">
                {{ invoice.shippingAddress.zipcode }}
              </span>
            </div>
          </div>
        </div>
      </div>
      <ul class="invoice__list-items">
        <!-- hats -->
        <li
          v-for="hat in invoice.hats"
          :key="hat.slug"
          class="list-item">
          <div
            class="list-item__details">
            <div class="list-item__name">
              {{ getHatName(hat.slug) }}
            </div>
            <div class="list-item__price-each">
              {{ hat.quantity }} @ {{ hat.unitPrice | price }}/each
            </div>
            <div class="list-item__total-price">
              Total: {{ hat.quantity * hat.unitPrice | price }}
            </div>
          </div>
        </li>
        <!-- patches -->
        <li
          v-for="patch in invoice.patches"
          :key="patch.imageId"
          class="list-item">
          <div
            class="list-item__details">
            <div class="list-item__name">
              {{ getPatchName(patch) }}
            </div>
            <div class="list-item__price-each">
              {{ patch.quantity }} @ {{ patch.unitPrice | price }}/each
            </div>
            <div class="list-item__total-price">
              Total: {{ patch.quantity * patch.unitPrice | price }}
            </div>
          </div>
        </li>
        <!-- custom patches -->
        <li
          v-for="patch in invoice.customPatches"
          :key="patch.id"
          class="list-item">
          <div
            class="list-item__details">
            <div class="list-item__name">
              custom patch
            </div>
            <div class="list-item__price-each">
              {{ patch.quantity }} @ {{ patch.unitPrice | price }}/each
            </div>
            <div class="list-item__total-price">
              Total: {{ patch.quantity * patch.unitPrice | price }}
            </div>
          </div>
        </li>
      </ul>
      <div class="invoice__footer">
        <div class="invoice__shipping-info">
          Usually ships within 1 business day<br>
          Estimated delivery: 2-3 business days
        </div>
        <ul class="invoice-totals">
          <li class="invoice-totals__subtotal">
            <span>subtotal</span>
            <span>{{ invoice.subtotal | price }}</span>
          </li>
          <li
            v-if="invoice.discount && invoice.discount!==0"
            class="invoice-totals__discount">
            <span>
              total discount
            </span>
            <span>
              -{{ invoice.discount | price }}
            </span>
          </li>
          <li class="invoice-totals__shipping">
            <span>shipping</span>
            <span>free!</span>
          </li>
          <li class="invoice-totals__taxes">
            <span>Sales Tax</span>
            <span v-if="invoice.salesTax">{{ invoice.salesTax | price }}</span>
            <span v-if="!invoice.salesTax">None</span>
          </li>
          <li class="invoice-totals__total">
            <!-- [!] Actual totals, calculated on the server -->
            <span>
              total
            </span>
            <span>
              {{ invoice.total | price }}
            </span>
          </li>
        </ul>
      </div>
    </div>

    <div
      v-if="invoice"
      class="order-confirmation__print-row">
      <v-btn
        large
        block
        outline
        color="primary"
        class="order-confirmation__print-button"
        @click="printPage">
        <v-icon>print</v-icon>
        print this page
      </v-btn>
      <span class="order-confirmation__print-warning">WARNING: If you navigate away from this page, you will not be able to return.</span>
    </div>
  </v-container>
</template>

<script>
import MHLogo from '../components/MHLogo.vue';

import api from '../utils/api';

export default {
  name: 'OrderConfirmation',
  components: {
    MHLogo,
  },
  data() {
    return {
      hatData: [],
      patchData: [],
      invoice: {},
    };
  },
  computed: {
    isAuthenticated() {
      return this.$store.getters['auth/isAuthenticated'];
    },
    vuexInvoice() {
      return this.$store.state.cart.invoice;
    },
  },
  beforeRouteEnter(to, from, next) {
    // can only access from checkout
    if (!to.params.fromCheckout) {
      next((vm) => {
        vm.$router.replace('/');
      });
    }
    next();
  },
  created() {
    // make local copy of Invoice
    this.invoice = JSON.parse(JSON.stringify(this.vuexInvoice));
    // get data about cart items
    this.getHatData();
    this.getPatchData();
    // delete vuex cart and invoice
    this.$store.dispatch('cart/clearCart');
    this.$store.dispatch('cart/clearInvoice');
    // refresh user data
    if (this.isAuthenticated) {
      const id = this.$store.state.user.currentUser.publicId;
      this.$store.dispatch('user/getUser', id);
    }
  },
  methods: {
    printPage() {
      window.print();
    },
    getHatData() {
      for (let i = 0; i < this.invoice.hats.length; i++) {
        api
          .getData(`/hats/${this.invoice.hats[i].slug}`)
          .then((res) => {
            this.hatData.push(res.data);
          });
      }
    },
    getPatchData() {
      for (let i = 0; i < this.invoice.patches.length; i++) {
        const patch = this.invoice.patches[i];
        if (patch.patchGroup === 'catalog') {
          api
            .getData(`/catalog/${patch.imageId}`)
            .then((res) => {
              this.patchData.push(res.data);
            });
        }
        if (patch.patchGroup === 'submissions') {
          api
            .getData(`/submissions/${patch.imageId}`)
            .then((res) => {
              this.patchData.push(res.data);
            });
        }
      }
    },
    getHatName(slug) {
      const hat = this.hatData.find(h => h.slug === slug);
      if (hat) {
        return hat.name;
      }
      return '';
    },
    getPatchName(patch) {
      if (patch.name) return patch.name;
      if (patch.patchGroup === 'savedCustoms') return 'Custom Patch';
      if (patch.imageId) {
        const name = this.patchData.find(p => p.imageId === patch.imageId);
        if (name) return name;
      }
      return 'Patch';
    },
  },
};
</script>

<style lang="stylus" scoped>
@require('../stylus/base')

.order-confirmation
  display: flex
  flex-flow: column nowrap
  align-items: center
  justify-content: flex-start
  &__print-logo
    display: none
  &__header
    //
  &__body
    text-align: center
  &__print-row
    padding: $base-padding
    font-size: $font-size--small
    text-align: center
    color: $accent--dark

.address
  display: flex
  flex-flow: column nowrap
  text-transform: capitalize
  &__state
    text-transform: uppercase

.invoice
  width: 100%
  padding: $base-padding
  box-shadow: $base-box-shadow
  &__header
    display: flex
    flex-flow: row wrap
    border-bottom: 1px solid $accent
  &__subheader
    @extends $base-subheader
  &__order-number
    display: flex
    flex: 1 1 50%
    flex-flow: column nowrap
    padding-bottom: $base-padding*2
  &__order-date
    display: flex
    flex: 1 1 50%
    flex-flow: column nowrap
    align-items: flex-end
    padding-bottom: $base-padding*2
  &__shipping-address
    display: flex
    flex: 1 1 50%
    flex-flow: column nowrap
    padding-bottom: $base-padding*2
  &__payment-method
    display: flex
    flex: 1 1 50%
    flex-flow: column nowrap
    align-items: flex-end
    padding-bottom: $base-padding*2
  &__list-items
    list-style: none
    padding: 0
    padding-top: $base-padding
    padding-bottom: $base-padding*.5

.list-item
  display: flex
  flex: 1 1 0
  flex-flow: row nowrap
  align-items: center
  justify-content: space-between
  margin: 0
  padding-bottom: $base-padding*.5
  &+.list-item
    padding-top: $base-padding*.5
  &__image
    max-width: 7rem
  &__details
    display: flex
    flex-flow: row wrap
    align-items: flex-start
    justify-content: space-between
    width: 100%
    height: 100%
    padding: $base-padding*.5 $base-padding
  &__name
    width: 100%
    text-transform: capitalize
  &__total-price
    align-self: flex-end
    font-weight: $base-header-font-weight + 100
  &__price-each
    align-self: flex-end
    padding-bottom: $base-padding*.25

.invoice__footer
  display: flex
  flex-flow: row nowrap
  align-items: stretch
  justify-content: space-between
  padding-top: $base-padding
  border-top: 1px solid $accent
  @media screen and (max-width: 48rem)
    flex-direction: column-reverse

.invoice__shipping-info
  display: flex
  flex: 1 1 0
  flex-flow: column nowrap
  @media screen and (max-width: 48rem)
    flex-basis: 100%
    padding: $base-padding
    border: 1px solid $accent

.invoice-totals
  flex: 1 1 0
  list-style: none
  padding: 0
  @media screen and (max-width: 48rem)
    flex-basis: 100%
  &__subtotal,
  &__discount,
  &__shipping,
  &__taxes,
  &__total
    display: flex
    justify-content: space-between
    height: 3rem
    width: 100%
    padding: $base-padding
    font-weight: $base-font-weight
    font-size: 1rem
    text-transform: capitalize
  &__total
    font-size: 1.5rem
    font-weight: $base-header-font-weight
    border-top: 1px solid $accent


// PRINTER STYLES
@media print
  .order-confirmation
    padding: 0
    &__print-logo
      display: block
      padding-bottom: $base-padding
    &__header,
    &__body,
    &__track-order-row,
    &__print-row
      display: none
  .invoice
    box-shadow: none;
    &__shipping-info
      display: none

</style>
